import {Injectable, inject} from '@angular/core';
import {ActivatedRouteSnapshot, RouterStateSnapshot, Router} from '@angular/router';
import {Observable, of, map, catchError} from 'rxjs';
import {KeycloakAuthGuard, KeycloakService} from 'keycloak-angular';
import {HealthService} from './services/health.service';
import {RolesService} from './services/roles.service';
import {MaintenanceService} from './services/maintenance.service';

@Injectable({
  providedIn: 'root'
})
export class AuthGuard extends KeycloakAuthGuard {

  state: RouterStateSnapshot;

  constructor(protected router: Router,
              protected service: KeycloakService,
              protected health: HealthService,
              protected rolesService: RolesService) {
    super(router, service);
  }

  private maintenance = inject(MaintenanceService);

  /**
   * Mode maintenance actif : tout utilisateur non gestionnaire est redirigé
   * vers la page de maintenance, quelle que soit la destination demandée.
   * Les gestionnaires gardent l'accès complet pour pouvoir désactiver le mode.
   *
   * Renvoie un Observable qui n'émet qu'une fois l'état de maintenance connu :
   * le garde attend cette réponse avant d'autoriser la navigation, ce qui évite
   * d'afficher brièvement la page cible (le dashboard) avant de basculer vers
   * la maintenance.
   */
  checkMaintenanceAccess(): Observable<boolean> {
    if (this.rolesService.isGestionnaire()) {
      return of(true);
    }
    return this.maintenance.isEnabled().pipe(
      map(enabled => {
        if (enabled && !this.state.url.startsWith('/maintenance')
          && !this.state.url.startsWith('/public')
          && !this.state.url.startsWith('/erreur')) {
          this.router.navigate(['/maintenance']);
          return false;
        }
        return true;
      }),
      // Endpoint indisponible : comportement nominal (maintenance inactive)
      catchError(() => of(true))
    );
  }

  resolvePublicAccess(): boolean {
    if (this.state.url.startsWith('/erreur/503')) {
      this.router.navigate(['/']);
    }
    if (this.state.url.startsWith('/public')) {
      return true;
    }
    if (!this.authenticated) {
      this.service.login({
        redirectUri: window.location.origin + this.state.url,
      });
      return true;
    }
    return false;
  }

  checkReferentAccess(): void {
    if (!this.rolesService.isReferent() &&
      (this.state.url.startsWith('/utilisateurs') ||
        this.state.url.startsWith('/prestations') ||
        this.state.url.startsWith('/contrats')
      )) {
      this.router.navigate(['/']);
    }
  }

  checkGestionnaireAccess(): void {
    const isGestionnaire = this.rolesService.isGestionnaire();
    const startWithAdmin = this.state.url.startsWith('/admin');
    if (isGestionnaire && !startWithAdmin) {
      this.router.navigate(['/admin']);
    } else if (!isGestionnaire && startWithAdmin) {
      this.router.navigate(['/']);
    }
  }

  checkCollaborateurAccess(): void {
    if (this.rolesService.isCollaborateur() &&
      (!this.state.url.startsWith('/informations') &&
        !this.state.url.startsWith('/faq') &&
        !this.state.url.startsWith('/documentations') &&
        !this.state.url.startsWith('/notes-de-version') &&
        !this.state.url.startsWith('/outils'))) {
      this.router.navigate(['/informations']);
    }
  }

  resolveErrorAccess(): boolean {
    if (this.state.url.startsWith('/erreur/503')) {
      return true;
    } else {
      this.router.navigate(['/erreur/503']);
    }
    return false;
  }

  isAccessAllowed(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<boolean> {
    this.state = state;
    return new Promise((resolve) => {
      this.health.isHealthy().subscribe(() => {
        if (this.resolvePublicAccess()) {
          resolve(true);
          return;
        }
        if (this.keycloakAngular.isTokenExpired()) {
          this.service.login({
            redirectUri: window.location.origin + this.state.url,
          });
          return true;
        }
        this.checkReferentAccess();
        this.checkGestionnaireAccess();
        this.checkCollaborateurAccess();
        this.checkMaintenanceAccess().subscribe(allowed => resolve(allowed));
      }, error => {
        console.error(error);
        if (this.resolveErrorAccess()) {
          resolve(true);
        }
      });
    });
  }
}

